import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  Divider,
  CircularProgress,
  Button,
  Chip,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { User, UserRole } from '../types/user';
import { getCurrentUser } from '../services/userService';
import { counties, subCounties, facilities } from '../data/locations';
import { CHS_DOMAINS, CHS_SUBDOMAINS } from '../types/project-form';
import { Project } from '../types/project';
import { projectService } from '../services/api';
import TopBar from '../components/TopBar';

interface AnalyticsProps {
  onLogout: () => void;
}

interface AnalyticsFilters {
  county: string;
  subcounty: string;
  facility: string;
  domain: string;
  subdomain: string;
  dateRange: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

// Color mapping for CHS domains
const DOMAIN_COLORS: Record<string, string> = {
  'Domain 1: Leadership and Governance': '#1976d2',
  'Domain 2: Collaboration and Partnership': '#388e3c',
  'Domain 3: Community Health Workforce': '#f57c00',
  'Domain 4: Community Health Infrastructure': '#7b1fa2',
  'Domain 5: Community Health Information System': '#c2185b',
  'Domain 6: Health Products and Technologies': '#00796b',
  'Domain 7: Transport': '#5d4037',
  'Domain 8: Referral System': '#455a64',
  'Domain 9: Community Health Financing': '#e64a19',
  'Domain 10: Service Delivery': '#1565c0',
  'Domain 11: CHS Client Satisfaction': '#ad1457',
  'Domain 12: CHS CHW Satisfaction': '#6a1b9a'
};

const Analytics: React.FC<AnalyticsProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [showOngoingBreakdown, setShowOngoingBreakdown] = useState(false);
  const [filters, setFilters] = useState<AnalyticsFilters>({
    county: 'all',
    subcounty: 'all',
    facility: 'all',
    domain: 'all',
    subdomain: 'all',
    dateRange: 'all_time',
    fromDate: null,
    toDate: null
  });


  // Fetch projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const allProjects = await projectService.getAllProjects();
        setProjects(allProjects);
        setError(null);
      } catch (err) {
        console.error('Error fetching projects for analytics:', err);
        setError('Failed to fetch projects data');
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    
    // Initialize filters based on user role
    if (user) {
      if (user.role === UserRole.COUNTY_USER && user.county) {
        setFilters(prev => ({ ...prev, county: user.county || 'all' }));
      } else if (user.role === UserRole.SUBCOUNTY_USER && user.subCounty) {
        const subcountyData = subCounties.find(sc => sc.name === user.subCounty);
        const countyName = subcountyData?.countyName || 'all';
        setFilters(prev => ({ 
          ...prev, 
          county: countyName,
          subcounty: user.subCounty || 'all'
        }));
      }
    }
  }, []);

  // Filter projects based on current filters and user role
  const getFilteredProjects = (): Project[] => {
    let filteredProjects = projects;

    // Apply role-based filtering first
    if (currentUser) {
      if (currentUser.role === UserRole.FACILITY_USER && currentUser.facility) {
        filteredProjects = filteredProjects.filter(project => 
          project.facility === currentUser.facility
        );
      } else if (currentUser.role === UserRole.SUBCOUNTY_USER && currentUser.subCounty) {
        filteredProjects = filteredProjects.filter(project => 
          project.subcounty === currentUser.subCounty
        );
      } else if (currentUser.role === UserRole.COUNTY_USER && currentUser.county) {
        filteredProjects = filteredProjects.filter(project => 
          project.county === currentUser.county
        );
      }
    }

    // Apply UI filters
    if (filters.county !== 'all') {
      filteredProjects = filteredProjects.filter(project => 
        project.county === filters.county
      );
    }

    if (filters.subcounty !== 'all') {
      filteredProjects = filteredProjects.filter(project => 
        project.subcounty === filters.subcounty
      );
    }

    if (filters.facility !== 'all') {
      filteredProjects = filteredProjects.filter(project => 
        project.facility === filters.facility
      );
    }

    if (filters.domain !== 'all') {
      filteredProjects = filteredProjects.filter(project => 
        project.domain === filters.domain
      );
    }

    if (filters.subdomain !== 'all') {
      filteredProjects = filteredProjects.filter(project => 
        project.subDomain === filters.subdomain
      );
    }

    // Apply date range filter
    if (filters.dateRange === 'custom') {
      const fromDate = filters.fromDate?.startOf('day').toDate();
      const toDate = filters.toDate?.endOf('day').toDate();
      filteredProjects = filteredProjects.filter(project => {
        const projectDate = new Date(project.createdAt);
        if (fromDate && projectDate < fromDate) return false;
        if (toDate && projectDate > toDate) return false;
        return true;
      });
    } else if (filters.dateRange !== 'all_time') {
      const now = new Date();
      let startDate: Date;

      switch (filters.dateRange) {
        case 'last_week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last_month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        case 'last_3_months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          break;
        case 'last_6_months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
          break;
        case 'last_year':
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          break;
        case 'current_year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(0); // Beginning of time
      }

      filteredProjects = filteredProjects.filter(project => {
        const projectDate = new Date(project.createdAt);
        return projectDate >= startDate;
      });
    }

    return filteredProjects;
  };

  // Generate domain distribution data from filtered projects
  const getDomainDistributionData = () => {
    const filteredProjects = getFilteredProjects();
    const domainCounts: Record<string, number> = {};

    // Initialize all domains with 0 count
    CHS_DOMAINS.forEach(domain => {
      domainCounts[domain] = 0;
    });

    // Count projects by domain
    filteredProjects.forEach(project => {
      if (project.domain && domainCounts.hasOwnProperty(project.domain)) {
        domainCounts[project.domain]++;
      }
    });

    // Convert to chart data format and filter out domains with 0 projects
    return Object.entries(domainCounts)
      .filter(([_, count]) => count > 0)
      .map(([domain, count]) => ({
        domain: domain.replace('Domain ', 'D'), // Shorten for better display
        projects: count,
        fullName: domain
      }))
      .sort((a, b) => b.projects - a.projects); // Sort by project count descending
  };

  const domainDistributionData = getDomainDistributionData();

  // Generate subdomain distribution data for selected domain
  const getSubdomainDistributionData = () => {
    if (!selectedDomain) return [];
    
    const filteredProjects = getFilteredProjects().filter(project => 
      project.domain === selectedDomain
    );
    
    const subdomainCounts: Record<string, number> = {};
    const domainSubdomains = CHS_SUBDOMAINS[selectedDomain] || [];

    // If domain has no subdomains, show the domain itself as a subdomain
    if (domainSubdomains.length === 0) {
      return [{
        subdomain: selectedDomain.length > 20 ? selectedDomain.substring(0, 17) + '...' : selectedDomain,
        projects: filteredProjects.length,
        fullName: selectedDomain
      }];
    }

    // Initialize all subdomains for this domain with 0 count
    domainSubdomains.forEach(subdomain => {
      subdomainCounts[subdomain] = 0;
    });

    // Count projects by subdomain
    filteredProjects.forEach(project => {
      if (project.subDomain && subdomainCounts.hasOwnProperty(project.subDomain)) {
        subdomainCounts[project.subDomain]++;
      }
    });

    // Convert to chart data format and filter out subdomains with 0 projects
    return Object.entries(subdomainCounts)
      .filter(([_, count]) => count > 0)
      .map(([subdomain, count]) => ({
        subdomain: subdomain.length > 20 ? subdomain.substring(0, 17) + '...' : subdomain,
        projects: count,
        fullName: subdomain
      }))
      .sort((a, b) => b.projects - a.projects);
  };

  const subdomainDistributionData = getSubdomainDistributionData();

  // Generate status distribution data from filtered projects
  const getStatusDistributionData = () => {
    const filteredProjects = getFilteredProjects();
    const statusCounts: Record<string, number> = {
      'ongoing': 0,
      'completed': 0,
      'archived': 0
    };

    // Count projects by status
    filteredProjects.forEach(project => {
      if (project.status && statusCounts.hasOwnProperty(project.status)) {
        statusCounts[project.status]++;
      }
    });

    // Convert to chart data format and filter out statuses with 0 projects
    return Object.entries(statusCounts)
      .filter(([_, count]) => count > 0)
      .map(([status, count]) => ({
        status: status.charAt(0).toUpperCase() + status.slice(1),
        projects: count,
        color: getStatusColor(status)
      }))
      .sort((a, b) => b.projects - a.projects);
  };

  // Get color for status
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ongoing': return '#4CAF50';
      case 'completed': return '#2196F3';
      case 'archived': return '#FF9800';
      default: return '#757575';
    }
  };

  const statusDistributionData = getStatusDistributionData();

  // Generate pie chart data from status distribution data
  const projectStatusData = statusDistributionData.map(item => ({
    name: item.status,
    value: item.projects,
    color: item.color
  }));

  // Generate summary statistics from filtered projects
  const getSummaryStatistics = () => {
    const filteredProjects = getFilteredProjects();
    const totalProjects = filteredProjects.length;
    const ongoingProjects = filteredProjects.filter(p => p.status === 'ongoing').length;
    const completedProjects = filteredProjects.filter(p => p.status === 'completed').length;
    const archivedProjects = filteredProjects.filter(p => p.status === 'archived').length;

    return {
      totalProjects,
      ongoingProjects,
      completedProjects,
      archivedProjects
    };
  };

  const summaryStats = getSummaryStatistics();

  // Check if project is within one week of completion
  const isWithinOneWeek = (date: string) => {
    if (!date) return false;
    
    const endDate = new Date(date);
    const now = new Date();
    const diffDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    return diffDays >= 0 && diffDays <= 7;
  };

  // Check if project is past due
  const isPastDue = (date: string) => {
    if (!date) return false;
    
    const endDate = new Date(date);
    const now = new Date();
    const diffDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    return diffDays < 0;
  };

  // Check if project is stalled (no progress updates for 2+ months)
  const isStalled = (project: Project) => {
    if (!project.testCycles || project.testCycles.length === 0) {
      // No progress updates at all - check if project started more than 2 months ago
      const startDate = new Date(project.firstCycleDate || project.startDate || project.createdAt);
      const now = new Date();
      const diffDays = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      return diffDays > 60; // 2 months = ~60 days
    }
    
    // Check if last update was more than 2 months ago
    const lastUpdate = project.testCycles.reduce((latest: Date, cycle: any) => {
      const cycleDate = new Date(cycle.createdAt || cycle.startDate || cycle.endDate);
      return cycleDate > latest ? cycleDate : latest;
    }, new Date(project.createdAt));
    
    const now = new Date();
    const diffDays = Math.ceil((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays > 60;
  };

  // Check if project is abandoned (no progress updates for 4+ months)
  const isAbandoned = (project: Project) => {
    if (!project.testCycles || project.testCycles.length === 0) {
      // No progress updates at all - check if project started more than 4 months ago
      const startDate = new Date(project.firstCycleDate || project.startDate || project.createdAt);
      const now = new Date();
      const diffDays = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      return diffDays > 120; // 4 months = ~120 days
    }
    
    // Check if last update was more than 4 months ago
    const lastUpdate = project.testCycles.reduce((latest: Date, cycle: any) => {
      const cycleDate = new Date(cycle.createdAt || cycle.startDate || cycle.endDate);
      return cycleDate > latest ? cycleDate : latest;
    }, new Date(project.createdAt));
    
    const now = new Date();
    const diffDays = Math.ceil((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays > 120;
  };

  // Generate ongoing sub-status distribution data
  const getOngoingSubStatusData = () => {
    const filteredProjects = getFilteredProjects();
    const ongoingProjects = filteredProjects.filter(p => p.status === 'ongoing');
    
    console.log('DEBUG - ongoingProjects count:', ongoingProjects.length);
    
    let onCourse = 0;
    let pastDue = 0;
    let stalled = 0;
    let abandoned = 0;

    ongoingProjects.forEach(project => {
      if (isAbandoned(project)) {
        abandoned++;
      } else if (isStalled(project)) {
        stalled++;
      } else if (isPastDue(project.projectEndDate || project.endDate)) {
        pastDue++;
      } else {
        onCourse++;
      }
    });

    // Always include all four statuses, even with zero projects
    const data = [
      {
        status: 'On Course',
        projects: onCourse,
        color: '#4CAF50'
      },
      {
        status: 'Past Due',
        projects: pastDue,
        color: '#F44336'
      },
      {
        status: 'Stalled',
        projects: stalled,
        color: '#FF9800'
      },
      {
        status: 'Abandoned',
        projects: abandoned,
        color: '#9E9E9E'
      }
    ];

    return data;
  };

  const ongoingSubStatusData = getOngoingSubStatusData();

  // County and sub-county users don't see the status pie chart; they use the
  // bar chart instead, where clicking "Ongoing" drills into its sub-statuses.
  const hideStatusPieChart =
    currentUser?.role === UserRole.SUBCOUNTY_USER ||
    currentUser?.role === UserRole.COUNTY_USER;

  // Handle domain bar click
  const handleDomainClick = (data: any) => {
    if (data && data.fullName) {
      setSelectedDomain(data.fullName);
    }
  };

  // Handle back to domains view
  const handleBackToDomains = () => {
    setSelectedDomain(null);
  };

  // Handle status bar click - drill into Ongoing sub-statuses
  const handleStatusBarClick = (data: any) => {
    const status = data?.status ?? data?.payload?.status ?? data?.activePayload?.[0]?.payload?.status;
    if (status === 'Ongoing') {
      setShowOngoingBreakdown(true);
    }
  };

  // Handle back to status overview
  const handleBackToStatuses = () => {
    setShowOngoingBreakdown(false);
  };

  // Check if user has access to analytics
  const hasAnalyticsAccess = currentUser && [
    UserRole.NATIONAL_USER,
    UserRole.CQI_MANAGER,
    UserRole.COUNTY_USER,
    UserRole.SUBCOUNTY_USER
  ].includes(currentUser.role);

  if (!hasAnalyticsAccess) {
    return (
      <>
        <Box sx={{ flexGrow: 1 }}>
          <TopBar onLogout={onLogout} />
        </Box>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" color="error" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1">
              You don't have permission to access the Analytics dashboard.
            </Typography>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <TopBar onLogout={onLogout} />
      </Box>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} color="primary">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" gutterBottom>
              View Detailed Dashboard
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Comprehensive insights and performance metrics for quality improvement projects
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 4 }} elevation={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Filters
            </Typography>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => setFilters({
                county: 'all',
                subcounty: 'all',
                facility: 'all',
                domain: 'all',
                subdomain: 'all',
                dateRange: 'all_time',
                fromDate: null,
                toDate: null
              })}
              sx={{ textTransform: 'none' }}
            >
              Clear All Filters
            </Button>
          </Box>
          <Grid container spacing={3}>
            {/* County filter - only for NATIONAL_USER and CQI_MANAGER */}
            {(currentUser?.role === UserRole.NATIONAL_USER || currentUser?.role === UserRole.CQI_MANAGER) && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>County</InputLabel>
                  <Select
                    value={filters.county}
                    label="County"
                    onChange={(e) => {
                      const county = e.target.value;
                      setFilters(prev => ({ ...prev, county, subcounty: 'all', facility: 'all' }));
                    }}
                  >
                    <MenuItem value="all">All Counties</MenuItem>
                    {counties.map(county => (
                      <MenuItem key={county.id} value={county.name}>{county.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Subcounty filter - for NATIONAL_USER, CQI_MANAGER, and COUNTY_USER */}
            {(currentUser?.role === UserRole.NATIONAL_USER || 
              currentUser?.role === UserRole.CQI_MANAGER || 
              currentUser?.role === UserRole.COUNTY_USER) && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Subcounty</InputLabel>
                  <Select
                    value={filters.subcounty}
                    label="Subcounty"
                    onChange={(e) => {
                      const subcounty = e.target.value;
                      setFilters(prev => ({ ...prev, subcounty, facility: 'all' }));
                    }}
                  >
                    <MenuItem value="all">All Subcounties</MenuItem>
                    {subCounties
                      .filter(subcounty => {
                        if (currentUser?.role === UserRole.COUNTY_USER && currentUser.county) {
                          return subcounty.countyName === currentUser.county;
                        }
                        return filters.county === 'all' || subcounty.countyName === filters.county;
                      })
                      .map(subcounty => (
                        <MenuItem key={subcounty.id} value={subcounty.name}>{subcounty.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Facility filter - for all except FACILITY_USER */}
            {currentUser?.role !== UserRole.FACILITY_USER && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>WIT</InputLabel>
                  <Select
                    value={filters.facility}
                    label="WIT"
                    onChange={(e) => setFilters(prev => ({ ...prev, facility: e.target.value }))}
                  >
                    <MenuItem value="all">All WITs</MenuItem>
                    {facilities
                      .filter(facility => {
                        if (currentUser?.role === UserRole.SUBCOUNTY_USER && currentUser.subCounty) {
                          return facility.subCountyName === currentUser.subCounty;
                        }
                        if (filters.subcounty !== 'all') {
                          return facility.subCountyName === filters.subcounty;
                        }
                        if (filters.county !== 'all') {
                          const countySubcounties = subCounties
                            .filter(sc => sc.countyName === filters.county)
                            .map(sc => sc.name);
                          return countySubcounties.includes(facility.subCountyName);
                        }
                        return true;
                      })
                      .map(facility => (
                        <MenuItem key={facility.id} value={facility.name}>{facility.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Domain filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Domain</InputLabel>
                <Select
                  value={filters.domain}
                  label="Domain"
                  onChange={(e) => {
                    const domain = e.target.value;
                    setFilters(prev => ({ ...prev, domain, subdomain: 'all' }));
                  }}
                >
                  <MenuItem value="all">All Domains</MenuItem>
                  {CHS_DOMAINS.map(domain => (
                    <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Subdomain filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Subdomain</InputLabel>
                <Select
                  value={filters.subdomain}
                  label="Subdomain"
                  onChange={(e) => setFilters(prev => ({ ...prev, subdomain: e.target.value }))}
                  disabled={filters.domain === 'all'}
                >
                  <MenuItem value="all">All Subdomains</MenuItem>
                  {filters.domain !== 'all' && CHS_SUBDOMAINS[filters.domain] && 
                    CHS_SUBDOMAINS[filters.domain].map(subdomain => (
                      <MenuItem key={subdomain} value={subdomain}>{subdomain}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>

            {/* Date Range filter */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={filters.dateRange}
                  label="Date Range"
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                >
                  <MenuItem value="last_week">Last Week</MenuItem>
                  <MenuItem value="last_month">Last Month</MenuItem>
                  <MenuItem value="last_3_months">Last 3 Months</MenuItem>
                  <MenuItem value="last_6_months">Last 6 Months</MenuItem>
                  <MenuItem value="last_year">Last Year</MenuItem>
                  <MenuItem value="current_year">Current Year</MenuItem>
                  <MenuItem value="all_time">All Time</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Custom date range pickers */}
            {filters.dateRange === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="From Date"
                      value={filters.fromDate}
                      onChange={(date) => setFilters(prev => ({ ...prev, fromDate: date ? dayjs(date) : null }))}
                      maxDate={filters.toDate || undefined}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="To Date"
                      value={filters.toDate}
                      onChange={(date) => setFilters(prev => ({ ...prev, toDate: date ? dayjs(date) : null }))}
                      minDate={filters.fromDate || undefined}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
              </>
            )}
          </Grid>
          
          {/* Active Filters Summary */}
          {(filters.county !== 'all' || filters.subcounty !== 'all' || filters.facility !== 'all' || 
            filters.domain !== 'all' || filters.subdomain !== 'all' || filters.dateRange !== 'all_time') && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Active Filters:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {filters.county !== 'all' && (
                  <Chip 
                    label={`County: ${filters.county}`} 
                    size="small" 
                    onDelete={() => setFilters(prev => ({ ...prev, county: 'all', subcounty: 'all', facility: 'all' }))}
                  />
                )}
                {filters.subcounty !== 'all' && (
                  <Chip 
                    label={`Subcounty: ${filters.subcounty}`} 
                    size="small" 
                    onDelete={() => setFilters(prev => ({ ...prev, subcounty: 'all', facility: 'all' }))}
                  />
                )}
                {filters.facility !== 'all' && (
                  <Chip 
                    label={`WIT: ${filters.facility}`} 
                    size="small" 
                    onDelete={() => setFilters(prev => ({ ...prev, facility: 'all' }))}
                  />
                )}
                {filters.domain !== 'all' && (
                  <Chip 
                    label={`Domain: ${filters.domain}`} 
                    size="small" 
                    onDelete={() => setFilters(prev => ({ ...prev, domain: 'all', subdomain: 'all' }))}
                  />
                )}
                {filters.subdomain !== 'all' && (
                  <Chip 
                    label={`Subdomain: ${filters.subdomain}`} 
                    size="small" 
                    onDelete={() => setFilters(prev => ({ ...prev, subdomain: 'all' }))}
                  />
                )}
                {filters.dateRange !== 'all_time' && (
                  <Chip 
                    label={filters.dateRange === 'custom'
                      ? `Date: ${filters.fromDate ? filters.fromDate.format('YYYY-MM-DD') : '...'} to ${filters.toDate ? filters.toDate.format('YYYY-MM-DD') : '...'}`
                      : `Date: ${filters.dateRange.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
                    size="small" 
                    onDelete={() => setFilters(prev => ({ ...prev, dateRange: 'all_time', fromDate: null, toDate: null }))}
                  />
                )}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Charts Grid */}
        <Grid container spacing={4}>
          {/* Project Status Distribution - Pie Chart - Hidden for SUBCOUNTY_USER and COUNTY_USER */}
          {!hideStatusPieChart && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Project Status Distribution" />
                <CardContent>
                  {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <CircularProgress />
                    </Box>
                  ) : error ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="error">{error}</Typography>
                    </Box>
                  ) : projectStatusData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No projects found for selected filters</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={projectStatusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry: any) => `${entry.name} ${entry.percent ? (entry.percent * 100).toFixed(0) : 0}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {projectStatusData.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Project Status Distribution - Bar Chart - click "Ongoing" to drill into sub-statuses */}
          <Grid item xs={12} md={hideStatusPieChart ? 12 : 6}>
            <Card>
              <CardHeader
                title={showOngoingBreakdown ? "Ongoing Projects Breakdown" : "Projects by Status"}
                action={showOngoingBreakdown && (
                  <Box
                    component="span"
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      fontSize: '0.875rem'
                    }}
                    onClick={handleBackToStatuses}
                  >
                    ← Back to Statuses
                  </Box>
                )}
              />
              <CardContent>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography color="error">{error}</Typography>
                  </Box>
                ) : showOngoingBreakdown ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ongoingSubStatusData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="status"
                        angle={0}
                        textAnchor="middle"
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name) => [
                          `${value} projects`,
                          'Number of Projects'
                        ]}
                        labelFormatter={(label) => `Status: ${label}`}
                      />
                      <Legend />
                      <Bar
                        dataKey="projects"
                        name="Number of Projects"
                        radius={[4, 4, 0, 0]}
                      >
                        {ongoingSubStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : statusDistributionData.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography color="textSecondary">No projects found for selected filters</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statusDistributionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="status"
                        angle={0}
                        textAnchor="middle"
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name) => [
                          `${value} projects`,
                          'Number of Projects'
                        ]}
                        labelFormatter={(label) => `Status: ${label}`}
                      />
                      <Legend />
                      <Bar
                        dataKey="projects"
                        name="Number of Projects"
                        radius={[4, 4, 0, 0]}
                        cursor="pointer"
                        onClick={handleStatusBarClick}
                      >
                        {statusDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
                {!showOngoingBreakdown && statusDistributionData.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Click on the Ongoing bar to view its sub-statuses
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Ongoing Sub-Status Distribution - hidden when pie chart is hidden (sub-statuses are reachable via drill-down) */}
          {ongoingSubStatusData.length > 0 && !hideStatusPieChart && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Ongoing Projects Breakdown" />
                <CardContent>
                  {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <CircularProgress />
                    </Box>
                  ) : error ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="error">{error}</Typography>
                    </Box>
                  ) : ongoingSubStatusData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No ongoing projects found</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={ongoingSubStatusData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="status" 
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name) => [
                              `${value} projects`,
                              'Number of Projects'
                            ]}
                            labelFormatter={(label) => `Status: ${label}`}
                          />
                          <Legend />
                          <Bar 
                            dataKey="projects" 
                            name="Number of Projects"
                            radius={[4, 4, 0, 0]}
                          >
                            {ongoingSubStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Domain/Subdomain Distribution */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title={selectedDomain ? `Subdomains in ${selectedDomain}` : "Projects by Domain"}
                action={selectedDomain && (
                  <Box 
                    component="span" 
                    sx={{ 
                      cursor: 'pointer', 
                      color: 'primary.main',
                      textDecoration: 'underline',
                      fontSize: '0.875rem'
                    }}
                    onClick={handleBackToDomains}
                  >
                    ← Back to Domains
                  </Box>
                )}
              />
              <CardContent>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                    <Typography color="error">{error}</Typography>
                  </Box>
                ) : selectedDomain ? (
                  // Subdomain view
                  subdomainDistributionData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No projects found in {selectedDomain} subdomains</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={subdomainDistributionData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="subdomain" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis />
                        <Tooltip 
                          formatter={(value, name, props) => [
                            `${value} projects`,
                            props.payload?.fullName || name
                          ]}
                          labelFormatter={(label) => {
                            const item = subdomainDistributionData.find(d => d.subdomain === label);
                            return item?.fullName || label;
                          }}
                        />
                        <Legend />
                        <Bar 
                          dataKey="projects" 
                          fill={DOMAIN_COLORS[selectedDomain || ''] || '#4CAF50'}
                          name="Number of Projects"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )
                ) : (
                  // Domain view
                  domainDistributionData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                      <Typography color="textSecondary">No projects found for selected filters</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart data={domainDistributionData} onClick={handleDomainClick}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="domain" 
                          angle={-45}
                          textAnchor="end"
                          height={120}
                          fontSize={12}
                          interval={0}
                        />
                        <YAxis />
                        <Tooltip 
                          formatter={(value, name, props) => [
                            `${value} projects`,
                            props.payload?.fullName || name
                          ]}
                          labelFormatter={(label) => {
                            const item = domainDistributionData.find(d => d.domain === label);
                            return item?.fullName || label;
                          }}
                        />
                        <Legend />
                        <Bar 
                          dataKey="projects" 
                          name="Number of Projects"
                          radius={[4, 4, 0, 0]}
                          cursor="pointer"
                          onClick={handleDomainClick}
                        >
                          {domainDistributionData.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={DOMAIN_COLORS[entry.fullName] || '#1976d2'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )
                )}
                {!selectedDomain && domainDistributionData.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Click on a bar to view subdomains
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>



          {/* Summary Statistics */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Summary Statistics" />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h3" color="primary">
                        {summaryStats.totalProjects}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Projects
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h3" color="success.main">
                        {summaryStats.ongoingProjects}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ongoing Projects
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h3" color="info.main">
                        {summaryStats.completedProjects}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Completed Projects
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Analytics;
